import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ArrowLeft, RefreshCw, DollarSign, TrendingUp, TrendingDown, 
  Upload, Download, Calendar, CreditCard, Building, 
  ChevronDown, ChevronRight, Trash2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MONTHS_FULL = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

// Dias por mês (considerando ano não bissexto)
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const AdminCashFlow = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [activeTab, setActiveTab] = useState('anual');
  
  // Dados
  const [reservations, setReservations] = useState([]);
  const [manualReservations, setManualReservations] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [stoneCSVData, setStoneCSVData] = useState([]);
  const [sumupData, setSumupData] = useState([]);
  const [packages, setPackages] = useState([]);
  const [generalExpenses, setGeneralExpenses] = useState([]);
  const [operations, setOperations] = useState([]);
  const [operationStatuses, setOperationStatuses] = useState({});
  // Parcelas flattenadas do novo endpoint
  const [flatInstallments, setFlatInstallments] = useState([]);
  
  // Modais
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showUploadSumupModal, setShowUploadSumupModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadSumupFile, setUploadSumupFile] = useState(null);
  
  // Expandir seções
  const [expandReceitas, setExpandReceitas] = useState(true);
  const [expandDespesas, setExpandDespesas] = useState(true);

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [resResponse, manualResponse, pkgResponse, expResponse, stoneResponse, sumupResponse, generalExpResponse, opsResponse, flatInstallmentsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reservations/`),
        axios.get(`${BACKEND_URL}/api/manual-reservations/`),
        axios.get(`${BACKEND_URL}/api/packages/?active_only=false`),
        axios.get(`${BACKEND_URL}/api/cash-flow/expenses`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/cash-flow/stone-csv`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/cash-flow/sumup-data`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/cash-flow/general-expenses`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/operational-results/operations`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/asaas/payments/installments-flat`).catch(() => ({ data: [] }))
      ]);
      
      setReservations(resResponse.data);
      setManualReservations(manualResponse.data);
      setPackages(pkgResponse.data);
      setExpenses(expResponse.data);
      setStoneCSVData(stoneResponse.data);
      setSumupData(sumupResponse.data || []);
      setGeneralExpenses(generalExpResponse.data);
      setOperations(opsResponse.data || []);
      setFlatInstallments(flatInstallmentsResponse.data || []);
      
      // Carregar status das operações para saber budget_people
      const opsData = opsResponse.data || [];
      if (opsData.length > 0) {
        try {
          const statusPromises = opsData.map(op => 
            axios.get(`${BACKEND_URL}/api/operational-results/operation-status/${op.id}`).catch(() => null)
          );
          const statusResults = await Promise.all(statusPromises);
          const statusMap = {};
          statusResults.forEach((res, idx) => {
            if (res?.data) {
              const op = opsData[idx];
              // Usar a chave combinada package_id_date_id para consistência
              const opKey = `${op.package_id}_${op.date_id}`;
              statusMap[opKey] = res.data;
              // Também adicionar com o id original para compatibilidade
              statusMap[op.id] = res.data;
            }
          });
          setOperationStatuses(statusMap);
        } catch (e) {
          console.error('Erro ao carregar status:', e);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price || 0);
  };

  // Verificar se é ano bissexto
  const isLeapYear = (year) => {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  };

  // Obter dias no mês
  const getDaysInMonth = (month, year) => {
    if (month === 1 && isLeapYear(year)) return 29;
    return DAYS_IN_MONTH[month];
  };

  // Processar receitas do ASAAS usando parcelas flattenadas
  // PIX = pagamentos PIX (à vista)
  // Boleto = parcelas PAGAS de boleto
  const asaasRevenues = useMemo(() => {
    const revenues = {
      pix: Array(12).fill(0),
      boleto: Array(12).fill(0),
      cartao: Array(12).fill(0),
      pixByDay: Array(12).fill(null).map(() => Array(31).fill(0)),
      boletoByDay: Array(12).fill(null).map(() => Array(31).fill(0)),
      cartaoByDay: Array(12).fill(null).map(() => Array(31).fill(0))
    };
    
    // Processar parcelas flattenadas - apenas as PAGAS
    flatInstallments.forEach(installment => {
      const isPaid = ['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH'].includes(installment.status);
      if (!isPaid) return;
      
      // Usar data de pagamento real, senão usar due_date, senão created_at
      const paidDateStr = installment.paid_at || installment.due_date;
      if (!paidDateStr) return;
      
      const date = new Date(paidDateStr);
      if (isNaN(date.getTime())) return;
      if (date.getFullYear() !== selectedYear) return;
      
      const monthIdx = date.getMonth();
      const day = date.getDate() - 1;
      const amount = installment.amount || installment.value || 0;
      const billingType = (installment.billing_type || '').toUpperCase();
      
      // Categorizar por tipo de pagamento
      if (billingType === 'PIX') {
        revenues.pix[monthIdx] += amount;
        if (day >= 0 && day < 31) revenues.pixByDay[monthIdx][day] += amount;
      } else if (billingType === 'BOLETO') {
        revenues.boleto[monthIdx] += amount;
        if (day >= 0 && day < 31) revenues.boletoByDay[monthIdx][day] += amount;
      } else if (billingType === 'CREDIT_CARD') {
        revenues.cartao[monthIdx] += amount;
        if (day >= 0 && day < 31) revenues.cartaoByDay[monthIdx][day] += amount;
      } else {
        // Se não tem tipo definido, colocar como PIX por padrão
        revenues.pix[monthIdx] += amount;
        if (day >= 0 && day < 31) revenues.pixByDay[monthIdx][day] += amount;
      }
    });
    
    // Também processar reservas confirmadas que NÃO estão na collection payments
    // (reservas antigas ou pagas por outros meios)
    const processedReservationIds = new Set(flatInstallments.map(i => i.reservation_id).filter(Boolean));
    
    reservations.forEach(res => {
      // Se já foi processada via flatInstallments, pular
      if (processedReservationIds.has(res.id)) return;
      
      // Verificar se está paga
      const paidStatuses = ['paid', 'approved', 'RECEIVED', 'CONFIRMED', 'confirmed', 'RECEIVED_IN_CASH'];
      const isPaid = paidStatuses.includes(res.payment_status) || 
                     res.status === 'confirmed' ||
                     res.status === 'Confirmada';
      
      if (!isPaid) return;
      
      const paidDate = res.paid_at || res.payment_date || res.created_at;
      if (!paidDate) return;
      
      const date = new Date(paidDate);
      if (isNaN(date.getTime())) return;
      if (date.getFullYear() !== selectedYear) return;
      
      const monthIdx = date.getMonth();
      const day = date.getDate() - 1;
      
      // Determinar método de pagamento
      let method = res.payment_method?.toLowerCase() || '';
      if (!method && res.pix_qr_code) method = 'pix';
      if (!method && res.billing_type === 'PIX') method = 'pix';
      if (!method && res.billing_type === 'BOLETO') method = 'boleto';
      if (!method) method = 'pix';
      
      const amount = res.final_total || res.total_amount || 0;
      
      if (method.includes('pix')) {
        revenues.pix[monthIdx] += amount;
        if (day >= 0 && day < 31) revenues.pixByDay[monthIdx][day] += amount;
      } else if (method.includes('boleto')) {
        revenues.boleto[monthIdx] += amount;
        if (day >= 0 && day < 31) revenues.boletoByDay[monthIdx][day] += amount;
      } else if (method.includes('cart')) {
        revenues.cartao[monthIdx] += amount;
        if (day >= 0 && day < 31) revenues.cartaoByDay[monthIdx][day] += amount;
      } else {
        revenues.pix[monthIdx] += amount;
        if (day >= 0 && day < 31) revenues.pixByDay[monthIdx][day] += amount;
      }
    });
    
    return revenues;
  }, [reservations, flatInstallments, selectedYear]);
  
  // Processar parcelas PENDENTES a vencer (boletos futuros)
  const pendingInstallmentsRevenue = useMemo(() => {
    const totals = Array(12).fill(0);
    const byDay = Array(12).fill(null).map(() => Array(31).fill(0));
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Usar parcelas flattenadas - apenas PENDING e com due_date futuro
    flatInstallments.forEach(installment => {
      if (installment.status !== 'PENDING') return;
      
      const dueDateStr = installment.due_date;
      if (!dueDateStr) return;
      
      const dueDate = new Date(dueDateStr);
      if (isNaN(dueDate.getTime())) return;
      dueDate.setHours(0, 0, 0, 0);
      
      // Apenas parcelas FUTURAS (não vencidas)
      if (dueDate < today) return;
      
      if (dueDate.getFullYear() !== selectedYear) return;
      
      const monthIdx = dueDate.getMonth();
      const day = dueDate.getDate() - 1;
      const amount = installment.amount || installment.value || 0;
      
      totals[monthIdx] += amount;
      if (day >= 0 && day < 31) {
        byDay[monthIdx][day] += amount;
      }
    });
    
    return { totals, byDay };
  }, [flatInstallments, selectedYear]);

  // Processar parcelas VENCIDAS (boletos atrasados)
  const overdueInstallmentsRevenue = useMemo(() => {
    const totals = Array(12).fill(0);
    const byDay = Array(12).fill(null).map(() => Array(31).fill(0));
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Usar parcelas flattenadas - apenas OVERDUE ou PENDING com due_date passado
    flatInstallments.forEach(installment => {
      // Incluir tanto OVERDUE quanto PENDING (que pode estar vencido)
      if (!['PENDING', 'OVERDUE'].includes(installment.status)) return;
      
      const dueDateStr = installment.due_date;
      if (!dueDateStr) return;
      
      const dueDate = new Date(dueDateStr);
      if (isNaN(dueDate.getTime())) return;
      dueDate.setHours(0, 0, 0, 0);
      
      // Apenas parcelas VENCIDAS (data já passou)
      if (dueDate >= today) return;
      
      if (dueDate.getFullYear() !== selectedYear) return;
      
      const monthIdx = dueDate.getMonth();
      const day = dueDate.getDate() - 1;
      const amount = installment.amount || installment.value || 0;
      
      totals[monthIdx] += amount;
      if (day >= 0 && day < 31) {
        byDay[monthIdx][day] += amount;
      }
    });
    
    return { totals, byDay };
  }, [flatInstallments, selectedYear]);

  // Processar receitas INTER PAGO (parcelas com payment_source = 'INTER' e status = 'pago')
  const interRevenues = useMemo(() => {
    const revenues = Array(12).fill(0);
    const byDay = Array(12).fill(null).map(() => Array(31).fill(0));
    
    manualReservations.forEach(res => {
      if (res.installments?.length > 0) {
        res.installments.forEach(inst => {
          const source = (inst.payment_source || 'INTER').toUpperCase();
          if (source !== 'INTER') return;
          if (inst.status !== 'pago') return;
          
          const paidDate = inst.paid_date || inst.due_date;
          if (!paidDate) return;
          
          const date = new Date(paidDate);
          if (date.getFullYear() !== selectedYear) return;
          
          const monthIdx = date.getMonth();
          const day = date.getDate() - 1;
          const value = parseFloat(inst.value) || 0;
          
          revenues[monthIdx] += value;
          if (day < 31) byDay[monthIdx][day] += value;
        });
      }
    });
    
    return { totals: revenues, byDay };
  }, [manualReservations, selectedYear]);

  // Processar receitas INTER PREVISTO (parcelas com payment_source = 'INTER' e status = 'pendente')
  const interPrevistoRevenues = useMemo(() => {
    const revenues = Array(12).fill(0);
    const byDay = Array(12).fill(null).map(() => Array(31).fill(0));
    
    manualReservations.forEach(res => {
      if (res.installments?.length > 0) {
        res.installments.forEach(inst => {
          const source = (inst.payment_source || 'INTER').toUpperCase();
          if (source !== 'INTER') return;
          if (inst.status === 'pago') return; // Apenas pendentes
          
          const expectedDate = inst.expected_date || inst.due_date;
          if (!expectedDate) return;
          
          const date = new Date(expectedDate);
          if (date.getFullYear() !== selectedYear) return;
          
          const monthIdx = date.getMonth();
          const day = date.getDate() - 1;
          const value = parseFloat(inst.value) || 0;
          
          revenues[monthIdx] += value;
          if (day < 31) byDay[monthIdx][day] += value;
        });
      }
    });
    
    return { totals: revenues, byDay };
  }, [manualReservations, selectedYear]);

  // Processar receitas SUMUP (parcelas com payment_source = 'SUMUP')
  const sumupRevenues = useMemo(() => {
    const pago = { totals: Array(12).fill(0), byDay: Array(12).fill(null).map(() => Array(31).fill(0)) };
    const previsto = { totals: Array(12).fill(0), byDay: Array(12).fill(null).map(() => Array(31).fill(0)) };
    
    manualReservations.forEach(res => {
      if (res.installments?.length > 0) {
        res.installments.forEach(inst => {
          const source = (inst.payment_source || 'INTER').toUpperCase();
          if (source !== 'SUMUP') return;
          
          const value = parseFloat(inst.value) || 0;
          
          if (inst.status === 'pago') {
            const paidDate = inst.paid_date || inst.due_date;
            if (!paidDate) return;
            
            const date = new Date(paidDate);
            if (date.getFullYear() !== selectedYear) return;
            
            const monthIdx = date.getMonth();
            const day = date.getDate() - 1;
            
            pago.totals[monthIdx] += value;
            if (day < 31) pago.byDay[monthIdx][day] += value;
          } else {
            const expectedDate = inst.expected_date || inst.due_date;
            if (!expectedDate) return;
            
            const date = new Date(expectedDate);
            if (date.getFullYear() !== selectedYear) return;
            
            const monthIdx = date.getMonth();
            const day = date.getDate() - 1;
            
            previsto.totals[monthIdx] += value;
            if (day < 31) previsto.byDay[monthIdx][day] += value;
          }
        });
      }
    });
    
    return { pago, previsto };
  }, [manualReservations, selectedYear]);

  // Processar receitas STONE (do CSV importado)
  const stoneRevenues = useMemo(() => {
    const revenues = Array(12).fill(0);
    const byDay = Array(12).fill(null).map(() => Array(31).fill(0));
    
    stoneCSVData.forEach(item => {
      const date = new Date(item.date);
      if (date.getFullYear() !== selectedYear) return;
      
      const monthIdx = date.getMonth();
      const day = date.getDate() - 1;
      const value = item.value || 0;
      
      revenues[monthIdx] += value;
      if (day < 31) byDay[monthIdx][day] += value;
    });
    
    return { totals: revenues, byDay };
  }, [stoneCSVData, selectedYear]);

  // Processar dados SUMUP do Excel (separando Pago e Previsto)
  const sumupExcelRevenues = useMemo(() => {
    const pago = { totals: Array(12).fill(0), byDay: Array(12).fill(null).map(() => Array(31).fill(0)) };
    const previsto = { totals: Array(12).fill(0), byDay: Array(12).fill(null).map(() => Array(31).fill(0)) };
    
    sumupData.forEach(item => {
      if (!item.date || !item.value) return;
      
      const date = new Date(item.date);
      if (date.getFullYear() !== selectedYear) return;
      
      const monthIdx = date.getMonth();
      const day = date.getDate() - 1;
      const value = parseFloat(item.value) || 0;
      
      if (item.type === 'recebido') {
        // Dados da aba "Recebido" = PAGO
        pago.totals[monthIdx] += value;
        if (day < 31) pago.byDay[monthIdx][day] += value;
      } else if (item.type === 'previsto') {
        // Dados da aba "previsto" = PREVISTO
        previsto.totals[monthIdx] += value;
        if (day < 31) previsto.byDay[monthIdx][day] += value;
      }
    });
    
    return { pago, previsto };
  }, [sumupData, selectedYear]);

  // Processar despesas (dos Resultados Operacionais)
  const expensesByMonth = useMemo(() => {
    const totals = Array(12).fill(0);
    const byDay = Array(12).fill(null).map(() => Array(31).fill(0));
    
    expenses.forEach(exp => {
      if (exp.status !== 'pago') return;
      
      const paymentDate = exp.payment_date || exp.created_at;
      if (!paymentDate) return;
      
      const date = new Date(paymentDate);
      if (date.getFullYear() !== selectedYear) return;
      
      const monthIdx = date.getMonth();
      let day = date.getDate() - 1;
      const expenseDay = date.getDate(); // dia da despesa (1-indexed)
      const method = exp.payment_method || 'pix';
      
      // Para cartão, regra de corte:
      // - Até dia 16 (ou 14 em fevereiro): entra no dia 28 do próprio mês
      // - Após dia 16 (ou 14 em fevereiro): entra no dia 28 do mês seguinte
      if (method === 'cartao') {
        const cutoffDay = monthIdx === 1 ? 14 : 16; // Fevereiro = 14, outros meses = 16
        let paymentMonth = monthIdx;
        let paymentYear = selectedYear;
        
        // Se passou do dia de corte, vai para o mês seguinte
        if (expenseDay > cutoffDay) {
          paymentMonth = (monthIdx + 1) % 12;
          if (monthIdx === 11) paymentYear++; // Se dezembro, vai para ano seguinte
        }
        
        const installments = exp.card_installments || 1;
        const valuePerInstallment = exp.value / installments;
        
        for (let i = 0; i < installments; i++) {
          const installmentMonth = (paymentMonth + i) % 12;
          const installmentYear = paymentYear + Math.floor((paymentMonth + i) / 12);
          
          if (installmentYear === selectedYear) {
            totals[installmentMonth] += valuePerInstallment;
            byDay[installmentMonth][27] += valuePerInstallment; // dia 28
          }
        }
      } else {
        totals[monthIdx] += exp.value || 0;
        if (day < 31) byDay[monthIdx][day] += exp.value || 0;
      }
    });
    
    return { totals, byDay };
  }, [expenses, selectedYear]);

  // Processar despesas gerais (não operacionais)
  const generalExpensesByMonth = useMemo(() => {
    const totals = Array(12).fill(0);
    const byDay = Array(12).fill(null).map(() => Array(31).fill(0));
    
    generalExpenses.forEach(exp => {
      if (!exp.date) return;
      
      // Parse date como YYYY-MM-DD
      const dateParts = exp.date.split('-');
      if (dateParts.length !== 3) return;
      
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]) - 1; // 0-indexed
      const expenseDay = parseInt(dateParts[2]); // dia da despesa (1-indexed)
      let day = expenseDay - 1; // 0-indexed
      
      if (year !== selectedYear) return;
      
      // Para cartão, regra de corte:
      // - Até dia 16 (ou 14 em fevereiro): entra no dia 28 do próprio mês
      // - Após dia 16 (ou 14 em fevereiro): entra no dia 28 do mês seguinte
      const method = exp.payment_method || 'pix';
      if (method === 'cartao') {
        const cutoffDay = month === 1 ? 14 : 16; // Fevereiro = 14, outros meses = 16
        let paymentMonth = month;
        
        // Se passou do dia de corte, vai para o mês seguinte
        if (expenseDay > cutoffDay) {
          paymentMonth = (month + 1) % 12;
          // Se for dezembro e passar para janeiro, não conta neste ano
          if (month === 11) return;
        }
        
        totals[paymentMonth] += exp.value || 0;
        byDay[paymentMonth][27] += exp.value || 0; // dia 28
      } else {
        totals[month] += exp.value || 0;
        if (day < 31) byDay[month][day] += exp.value || 0;
      }
    });
    
    return { totals, byDay };
  }, [generalExpenses, selectedYear]);

  // Processar receitas orçadas de operações futuras
  const budgetedRevenues = useMemo(() => {
    const totals = Array(12).fill(0);
    const byDay = Array(12).fill(null).map(() => Array(31).fill(0));
    
    operations.forEach(op => {
      // Usar chave combinada para buscar status
      const opKey = `${op.package_id}_${op.date_id}`;
      const status = operationStatuses[opKey] || operationStatuses[op.id] || { status: 'aguardando_data', budget_people: 0 };
      
      // Só considerar operações futuras (aguardando_data)
      if (status.status !== 'aguardando_data') return;
      
      const budgetPeople = status.budget_people || 0;
      const avgPrice = op.avg_package_price || 0;
      const budgetRevenue = budgetPeople * avgPrice;
      const actualRevenue = op.total_revenue || 0;
      
      // Só adiciona se a receita orçada é maior que a realizada
      if (budgetRevenue <= actualRevenue) return;
      
      // Calcular mês de competência baseado na data da operação
      const dateStr = op.date || op.selected_date || '';
      let competenceMonth = -1;
      let competenceYear = selectedYear;
      
      // Parse date like "03 a 09 de Setembro/2026"
      const monthMatch = dateStr.match(/(janeiro|fevereiro|março|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro)\/(\d{4})/i);
      if (monthMatch) {
        const monthNames = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
        competenceMonth = monthNames.indexOf(monthMatch[1].toLowerCase());
        competenceYear = parseInt(monthMatch[2]);
      }
      
      if (competenceYear !== selectedYear || competenceMonth < 0) return;
      
      // Adiciona a diferença (orçado - realizado)
      const diffRevenue = budgetRevenue - actualRevenue;
      totals[competenceMonth] += diffRevenue;
      
      // Para visão diária, coloca no dia 15 como estimativa
      byDay[competenceMonth][14] += diffRevenue;
    });
    
    return { totals, byDay };
  }, [operations, operationStatuses, selectedYear]);

  // Calcular totais mensais
  const calcularTotalReceitasAsaas = (monthIdx) => {
    return asaasRevenues.pix[monthIdx] + asaasRevenues.boleto[monthIdx] + asaasRevenues.cartao[monthIdx];
  };

  const calcularTotalReceitas = (monthIdx) => {
    return calcularTotalReceitasAsaas(monthIdx) + 
           pendingInstallmentsRevenue.totals[monthIdx] + 
           overdueInstallmentsRevenue.totals[monthIdx] +
           interRevenues.totals[monthIdx] + 
           interPrevistoRevenues.totals[monthIdx] +
           stoneRevenues.totals[monthIdx] + 
           sumupRevenues.pago.totals[monthIdx] +
           sumupExcelRevenues.pago.totals[monthIdx] +
           sumupExcelRevenues.previsto.totals[monthIdx] +
           budgetedRevenues.totals[monthIdx];
  };

  const calcularTotalDespesas = (monthIdx) => {
    return expensesByMonth.totals[monthIdx] + generalExpensesByMonth.totals[monthIdx];
  };

  const calcularSaldo = (monthIdx) => {
    return calcularTotalReceitas(monthIdx) - calcularTotalDespesas(monthIdx);
  };

  // Totais anuais
  const totalReceitasAnual = Array(12).fill(0).map((_, i) => calcularTotalReceitas(i)).reduce((a, b) => a + b, 0);
  const totalDespesasOperacionais = expensesByMonth.totals.reduce((a, b) => a + b, 0);
  const totalDespesasGerais = generalExpensesByMonth.totals.reduce((a, b) => a + b, 0);
  const totalDespesasAnual = totalDespesasOperacionais + totalDespesasGerais;
  const totalInterPagoAnual = interRevenues.totals.reduce((a, b) => a + b, 0);
  const totalInterPrevistoAnual = interPrevistoRevenues.totals.reduce((a, b) => a + b, 0);
  const totalStoneAnual = stoneRevenues.totals.reduce((a, b) => a + b, 0);
  // SUMUP combinado: Reservas Manuais + Excel
  const totalSumupPagoAnual = sumupRevenues.pago.totals.reduce((a, b) => a + b, 0) + sumupExcelRevenues.pago.totals.reduce((a, b) => a + b, 0);
  const totalSumupPrevistoAnual = sumupExcelRevenues.previsto.totals.reduce((a, b) => a + b, 0);
  const totalAsaasAnual = asaasRevenues.pix.reduce((a,b) => a+b, 0) + asaasRevenues.boleto.reduce((a,b) => a+b, 0) + asaasRevenues.cartao.reduce((a,b) => a+b, 0);
  const totalBudgetedAnual = budgetedRevenues.totals.reduce((a, b) => a + b, 0);
  const totalPendingInstallmentsAnual = pendingInstallmentsRevenue.totals.reduce((a, b) => a + b, 0);
  const totalOverdueInstallmentsAnual = overdueInstallmentsRevenue.totals.reduce((a, b) => a + b, 0);
  const saldoAnual = totalReceitasAnual - totalDespesasAnual;

  // Calcular totais diários para tabela mensal - incluindo ASAAS
  const calcularReceitaAsaasDia = (day) => {
    return (asaasRevenues.pixByDay[selectedMonth][day] || 0) + 
           (asaasRevenues.boletoByDay[selectedMonth][day] || 0) + 
           (asaasRevenues.cartaoByDay[selectedMonth][day] || 0);
  };

  const calcularReceitaDia = (day) => {
    return calcularReceitaAsaasDia(day) + 
           (pendingInstallmentsRevenue.byDay[selectedMonth][day] || 0) +
           (overdueInstallmentsRevenue.byDay[selectedMonth][day] || 0) +
           (interRevenues.byDay[selectedMonth][day] || 0) + 
           (interPrevistoRevenues.byDay[selectedMonth][day] || 0) +
           (stoneRevenues.byDay[selectedMonth][day] || 0) + 
           (sumupRevenues.pago.byDay[selectedMonth][day] || 0) +
           (sumupExcelRevenues.pago.byDay[selectedMonth][day] || 0) +
           (sumupExcelRevenues.previsto.byDay[selectedMonth][day] || 0) +
           (budgetedRevenues.byDay[selectedMonth][day] || 0);
  };

  const calcularDespesaDia = (day) => {
    return expensesByMonth.byDay[selectedMonth][day] + generalExpensesByMonth.byDay[selectedMonth][day];
  };

  const calcularSaldoDia = (day) => {
    return calcularReceitaDia(day) - calcularDespesaDia(day);
  };

  // Upload de planilha STONE
  const handleUploadFile = async () => {
    if (!uploadFile) {
      alert('Selecione um arquivo');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await axios.post(`${BACKEND_URL}/api/cash-flow/upload-stone-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert(`${response.data.records_imported || 0} registros importados com sucesso!`);
      setShowUploadModal(false);
      setUploadFile(null);
      await loadData();
    } catch (error) {
      console.error('Erro ao importar arquivo:', error);
      alert('Erro ao importar arquivo. Verifique o formato.');
    }
  };

  // Exportar para CSV
  const exportToCSV = () => {
    const rows = [];
    rows.push(['Descrição', ...MONTHS, 'TOTAL']);
    
    rows.push(['RECEITAS', ...Array(12).fill(''), '']);
    rows.push(['ASAAS (PIX + Boleto + Cartão)', ...Array(12).fill(0).map((_, i) => calcularTotalReceitasAsaas(i)), totalAsaasAnual]);
    rows.push(['BOLETOS A VENCER', ...pendingInstallmentsRevenue.totals, totalPendingInstallmentsAnual]);
    rows.push(['BOLETOS VENCIDOS', ...overdueInstallmentsRevenue.totals, totalOverdueInstallmentsAnual]);
    rows.push(['INTER (Pago)', ...interRevenues.totals, totalInterPagoAnual]);
    rows.push(['INTER (Previsto)', ...interPrevistoRevenues.totals, totalInterPrevistoAnual]);
    rows.push(['STONE (CSV)', ...stoneRevenues.totals, totalStoneAnual]);
    rows.push(['SUMUP (Pago)', ...sumupExcelRevenues.pago.totals.map((v, i) => v + sumupRevenues.pago.totals[i]), totalSumupPagoAnual]);
    rows.push(['SUMUP (Previsto)', ...sumupExcelRevenues.previsto.totals, totalSumupPrevistoAnual]);
    rows.push(['ORÇADO (Projeção)', ...budgetedRevenues.totals, totalBudgetedAnual]);
    rows.push(['TOTAL RECEITAS', ...Array(12).fill(0).map((_,i) => calcularTotalReceitas(i)), totalReceitasAnual]);
    
    rows.push(['', ...Array(12).fill(''), '']);
    rows.push(['DESPESAS', ...Array(12).fill(''), '']);
    rows.push(['Operacionais', ...expensesByMonth.totals, totalDespesasOperacionais]);
    rows.push(['Gerais', ...generalExpensesByMonth.totals, totalDespesasGerais]);
    rows.push(['TOTAL DESPESAS', ...Array(12).fill(0).map((_,i) => calcularTotalDespesas(i)), totalDespesasAnual]);
    
    rows.push(['', ...Array(12).fill(''), '']);
    rows.push(['SALDO', ...Array(12).fill(0).map((_,i) => calcularSaldo(i)), saldoAnual]);
    
    const csvContent = rows.map(row => row.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `FluxoCaixa_${selectedYear}.csv`;
    link.click();
  };

  // Upload de planilha SUMUP (Excel)
  const handleUploadSumupFile = async () => {
    if (!uploadSumupFile) {
      alert('Selecione um arquivo Excel (.xlsx)');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', uploadSumupFile);
      
      const response = await axios.post(`${BACKEND_URL}/api/cash-flow/upload-sumup-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert(response.data.message || `${response.data.records_imported || 0} registros importados com sucesso!`);
      setShowUploadSumupModal(false);
      setUploadSumupFile(null);
      await loadData();
    } catch (error) {
      console.error('Erro ao importar arquivo SUMUP:', error);
      alert('Erro ao importar arquivo. Verifique se é um arquivo Excel (.xlsx) válido.');
    }
  };

  const years = [2024, 2025, 2026, 2027];
  const daysInSelectedMonth = getDaysInMonth(selectedMonth, selectedYear);
  
  // Data atual para destacar
  const today = new Date();
  const currentDay = today.getDate();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  
  // Função para verificar se é o dia atual (para destaque na visão mensal)
  const isCurrentDay = (dayIndex) => {
    return selectedYear === currentYear && selectedMonth === currentMonth && (dayIndex + 1) === currentDay;
  };
  
  // Função para verificar se é o mês atual (para destaque na visão anual)
  const isCurrentMonth = (monthIndex) => {
    return selectedYear === currentYear && monthIndex === currentMonth;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  // Componente da tabela mensal (dias 1-16)
  const MonthlyTablePart1 = () => (
    <table className="w-full border-collapse text-xs">
      <thead>
        <tr className="bg-[#003366] text-white">
          <th className="p-2 text-left font-semibold sticky left-0 bg-[#003366] z-20 min-w-[140px]">Descrição</th>
          {Array(16).fill(0).map((_, i) => (
            <th 
              key={i} 
              className={`p-1 text-center font-semibold min-w-[60px] ${
                i + 1 > daysInSelectedMonth 
                  ? 'bg-gray-800' 
                  : isCurrentDay(i) 
                    ? 'bg-yellow-400 text-gray-900' 
                    : ''
              }`}
            >
              {i + 1 <= daysInSelectedMonth ? i + 1 : ''}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {/* RECEITAS */}
        <tr className="bg-green-100 font-bold">
          <td className="p-1 sticky left-0 bg-green-100 z-10">💰 RECEITAS</td>
          {Array(16).fill(0).map((_, i) => {
            const valor = calcularReceitaDia(i);
            return (
              <td key={i} className={`p-1 text-right ${valor < 0 ? 'text-red-700' : 'text-green-700'} ${i + 1 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                {i + 1 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
              </td>
            );
          })}
        </tr>
        {/* ASAAS PIX */}
        <tr className="bg-purple-50">
          <td className="p-1 pl-4 sticky left-0 bg-purple-50 z-10">💳 ASAAS PIX</td>
          {Array(16).fill(0).map((_, i) => {
            const valor = asaasRevenues.pixByDay[selectedMonth][i] || 0;
            return (
              <td key={i} className={`p-1 text-right ${valor < 0 ? 'text-red-700' : 'text-purple-700'} ${i + 1 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                {i + 1 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
              </td>
            );
          })}
        </tr>
        {/* ASAAS BOLETO */}
        <tr className="bg-purple-50">
          <td className="p-1 pl-4 sticky left-0 bg-purple-50 z-10">📄 ASAAS Boleto</td>
          {Array(16).fill(0).map((_, i) => {
            const valor = asaasRevenues.boletoByDay[selectedMonth][i] || 0;
            return (
              <td key={i} className={`p-1 text-right ${valor < 0 ? 'text-red-700' : 'text-purple-700'} ${i + 1 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                {i + 1 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
              </td>
            );
          })}
        </tr>
        {/* ASAAS CARTÃO */}
        <tr className="bg-purple-50">
          <td className="p-1 pl-4 sticky left-0 bg-purple-50 z-10">💳 ASAAS Cartão</td>
          {Array(16).fill(0).map((_, i) => {
            const valor = asaasRevenues.cartaoByDay[selectedMonth][i] || 0;
            return (
              <td key={i} className={`p-1 text-right ${valor < 0 ? 'text-red-700' : 'text-purple-700'} ${i + 1 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                {i + 1 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
              </td>
            );
          })}
        </tr>
        {/* INTER */}
        <tr className="bg-orange-50">
          <td className="p-1 pl-4 sticky left-0 bg-orange-50 z-10">🏦 INTER</td>
          {Array(16).fill(0).map((_, i) => {
            const valor = interRevenues.byDay[selectedMonth][i] || 0;
            return (
              <td key={i} className={`p-1 text-right ${valor < 0 ? 'text-red-700' : 'text-orange-700'} ${i + 1 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                {i + 1 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
              </td>
            );
          })}
        </tr>
        {/* STONE */}
        <tr className="bg-cyan-50">
          <td className="p-1 pl-4 sticky left-0 bg-cyan-50 z-10">💳 STONE (CSV)</td>
          {Array(16).fill(0).map((_, i) => {
            const valor = stoneRevenues.byDay[selectedMonth][i] || 0;
            return (
              <td key={i} className={`p-1 text-right ${valor < 0 ? 'text-red-700' : 'text-cyan-700'} ${i + 1 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                {i + 1 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
              </td>
            );
          })}
        </tr>
        {/* BOLETOS A VENCER */}
        <tr className="bg-yellow-50">
          <td className="p-1 pl-4 sticky left-0 bg-yellow-50 z-10">📄 BOLETOS A VENCER</td>
          {Array(16).fill(0).map((_, i) => {
            const valor = pendingInstallmentsRevenue.byDay[selectedMonth][i] || 0;
            return (
              <td key={i} className={`p-1 text-right ${valor < 0 ? 'text-red-700' : 'text-yellow-700'} ${i + 1 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                {i + 1 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
              </td>
            );
          })}
        </tr>
        {/* BOLETOS VENCIDOS */}
        <tr className="bg-red-50">
          <td className="p-1 pl-4 sticky left-0 bg-red-50 z-10">⚠️ BOLETOS VENCIDOS</td>
          {Array(16).fill(0).map((_, i) => {
            const valor = overdueInstallmentsRevenue.byDay[selectedMonth][i] || 0;
            return (
              <td key={i} className={`p-1 text-right ${valor > 0 ? 'text-red-600' : 'text-gray-700'} ${i + 1 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                {i + 1 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
              </td>
            );
          })}
        </tr>
        
        {/* DESPESAS */}
        <tr className="bg-red-100 font-bold">
          <td className="p-1 sticky left-0 bg-red-100 z-10">💸 DESPESAS</td>
          {Array(16).fill(0).map((_, i) => {
            const valor = calcularDespesaDia(i);
            return (
              <td key={i} className={`p-1 text-right text-red-700 ${i + 1 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                {i + 1 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
              </td>
            );
          })}
        </tr>
        
        {/* SALDO */}
        <tr className="bg-blue-200 font-bold">
          <td className="p-1 sticky left-0 bg-blue-200 z-10">💰 SALDO</td>
          {Array(16).fill(0).map((_, i) => {
            const saldo = calcularSaldoDia(i);
            return (
              <td key={i} className={`p-1 text-right ${saldo >= 0 ? 'text-blue-700' : 'text-red-700'} ${i + 1 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                {i + 1 <= daysInSelectedMonth ? (saldo !== 0 ? formatPrice(saldo) : '-') : ''}
              </td>
            );
          })}
        </tr>
      </tbody>
    </table>
  );

  // Componente da tabela mensal (dias 17-31 + Total)
  const MonthlyTablePart2 = () => {
    // Calcular totais do mês
    const totalReceitasMes = Array(31).fill(0).map((_, i) => i < daysInSelectedMonth ? calcularReceitaDia(i) : 0).reduce((a, b) => a + b, 0);
    const totalAsaasPixMes = Array(31).fill(0).map((_, i) => i < daysInSelectedMonth ? (asaasRevenues.pixByDay[selectedMonth][i] || 0) : 0).reduce((a, b) => a + b, 0);
    const totalAsaasBoletoMes = Array(31).fill(0).map((_, i) => i < daysInSelectedMonth ? (asaasRevenues.boletoByDay[selectedMonth][i] || 0) : 0).reduce((a, b) => a + b, 0);
    const totalAsaasCartaoMes = Array(31).fill(0).map((_, i) => i < daysInSelectedMonth ? (asaasRevenues.cartaoByDay[selectedMonth][i] || 0) : 0).reduce((a, b) => a + b, 0);
    const totalInterMes = Array(31).fill(0).map((_, i) => i < daysInSelectedMonth ? interRevenues.byDay[selectedMonth][i] : 0).reduce((a, b) => a + b, 0);
    const totalStoneMes = Array(31).fill(0).map((_, i) => i < daysInSelectedMonth ? stoneRevenues.byDay[selectedMonth][i] : 0).reduce((a, b) => a + b, 0);
    const totalDespesasMes = Array(31).fill(0).map((_, i) => i < daysInSelectedMonth ? calcularDespesaDia(i) : 0).reduce((a, b) => a + b, 0);
    const saldoMes = totalReceitasMes - totalDespesasMes;
    
    return (
      <table className="w-full border-collapse text-xs mt-4">
        <thead>
          <tr className="bg-[#003366] text-white">
            <th className="p-2 text-left font-semibold sticky left-0 bg-[#003366] z-20 min-w-[140px]">Descrição</th>
            {Array(15).fill(0).map((_, i) => (
              <th 
                key={i} 
                className={`p-1 text-center font-semibold min-w-[60px] ${
                  i + 17 > daysInSelectedMonth 
                    ? 'bg-gray-800' 
                    : isCurrentDay(i + 16) 
                      ? 'bg-yellow-400 text-gray-900' 
                      : ''
                }`}
              >
                {i + 17 <= daysInSelectedMonth ? i + 17 : ''}
              </th>
            ))}
            <th className="p-2 text-center font-semibold min-w-[90px] bg-[#002244]">TOTAL</th>
          </tr>
        </thead>
        <tbody>
          {/* RECEITAS */}
          <tr className="bg-green-100 font-bold">
            <td className="p-1 sticky left-0 bg-green-100 z-10">💰 RECEITAS</td>
            {Array(15).fill(0).map((_, i) => {
              const valor = calcularReceitaDia(i + 16);
              return (
                <td key={i} className={`p-1 text-right ${valor < 0 ? 'text-red-700' : 'text-green-700'} ${i + 17 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                  {i + 17 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
                </td>
              );
            })}
            <td className="p-1 text-right text-green-700 bg-green-200 font-bold">{formatPrice(totalReceitasMes)}</td>
          </tr>
          {/* ASAAS PIX */}
          <tr className="bg-purple-50">
            <td className="p-1 pl-4 sticky left-0 bg-purple-50 z-10">💳 ASAAS PIX</td>
            {Array(15).fill(0).map((_, i) => {
              const valor = asaasRevenues.pixByDay[selectedMonth][i + 16] || 0;
              return (
                <td key={i} className={`p-1 text-right ${valor < 0 ? 'text-red-700' : 'text-purple-700'} ${i + 17 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                  {i + 17 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
                </td>
              );
            })}
            <td className="p-1 text-right text-purple-700 bg-purple-100 font-medium">{formatPrice(totalAsaasPixMes)}</td>
          </tr>
          {/* ASAAS BOLETO */}
          <tr className="bg-purple-50">
            <td className="p-1 pl-4 sticky left-0 bg-purple-50 z-10">📄 ASAAS Boleto</td>
            {Array(15).fill(0).map((_, i) => {
              const valor = asaasRevenues.boletoByDay[selectedMonth][i + 16] || 0;
              return (
                <td key={i} className={`p-1 text-right ${valor < 0 ? 'text-red-700' : 'text-purple-700'} ${i + 17 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                  {i + 17 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
                </td>
              );
            })}
            <td className="p-1 text-right text-purple-700 bg-purple-100 font-medium">{formatPrice(totalAsaasBoletoMes)}</td>
          </tr>
          {/* ASAAS CARTÃO */}
          <tr className="bg-purple-50">
            <td className="p-1 pl-4 sticky left-0 bg-purple-50 z-10">💳 ASAAS Cartão</td>
            {Array(15).fill(0).map((_, i) => {
              const valor = asaasRevenues.cartaoByDay[selectedMonth][i + 16] || 0;
              return (
                <td key={i} className={`p-1 text-right ${valor < 0 ? 'text-red-700' : 'text-purple-700'} ${i + 17 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                  {i + 17 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
                </td>
              );
            })}
            <td className="p-1 text-right text-purple-700 bg-purple-100 font-medium">{formatPrice(totalAsaasCartaoMes)}</td>
          </tr>
          {/* INTER */}
          <tr className="bg-orange-50">
            <td className="p-1 pl-4 sticky left-0 bg-orange-50 z-10">🏦 INTER</td>
            {Array(15).fill(0).map((_, i) => {
              const valor = interRevenues.byDay[selectedMonth][i + 16] || 0;
              return (
                <td key={i} className={`p-1 text-right ${valor < 0 ? 'text-red-700' : 'text-orange-700'} ${i + 17 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                  {i + 17 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
                </td>
              );
            })}
            <td className="p-1 text-right text-orange-700 bg-orange-100 font-medium">{formatPrice(totalInterMes)}</td>
          </tr>
          {/* STONE */}
          <tr className="bg-cyan-50">
            <td className="p-1 pl-4 sticky left-0 bg-cyan-50 z-10">💳 STONE (CSV)</td>
            {Array(15).fill(0).map((_, i) => {
              const valor = stoneRevenues.byDay[selectedMonth][i + 16] || 0;
              return (
                <td key={i} className={`p-1 text-right ${valor < 0 ? 'text-red-700' : 'text-cyan-700'} ${i + 17 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                  {i + 17 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
                </td>
              );
            })}
            <td className="p-1 text-right text-cyan-700 bg-cyan-100 font-medium">{formatPrice(totalStoneMes)}</td>
          </tr>
          {/* BOLETOS A VENCER */}
          <tr className="bg-yellow-50">
            <td className="p-1 pl-4 sticky left-0 bg-yellow-50 z-10">📄 BOLETOS A VENCER</td>
            {Array(15).fill(0).map((_, i) => {
              const valor = pendingInstallmentsRevenue.byDay[selectedMonth][i + 16] || 0;
              return (
                <td key={i} className={`p-1 text-right ${valor < 0 ? 'text-red-700' : 'text-yellow-700'} ${i + 17 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                  {i + 17 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
                </td>
              );
            })}
            <td className="p-1 text-right text-yellow-700 bg-yellow-100 font-medium">{formatPrice(Array(31).fill(0).map((_, i) => i < daysInSelectedMonth ? pendingInstallmentsRevenue.byDay[selectedMonth][i] : 0).reduce((a, b) => a + b, 0))}</td>
          </tr>
          {/* BOLETOS VENCIDOS */}
          <tr className="bg-red-50">
            <td className="p-1 pl-4 sticky left-0 bg-red-50 z-10">⚠️ BOLETOS VENCIDOS</td>
            {Array(15).fill(0).map((_, i) => {
              const valor = overdueInstallmentsRevenue.byDay[selectedMonth][i + 16] || 0;
              return (
                <td key={i} className={`p-1 text-right ${valor > 0 ? 'text-red-600' : 'text-gray-700'} ${i + 17 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                  {i + 17 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
                </td>
              );
            })}
            <td className="p-1 text-right text-red-600 bg-red-100 font-medium">{formatPrice(Array(31).fill(0).map((_, i) => i < daysInSelectedMonth ? overdueInstallmentsRevenue.byDay[selectedMonth][i] : 0).reduce((a, b) => a + b, 0))}</td>
          </tr>
          
          {/* DESPESAS */}
          <tr className="bg-red-100 font-bold">
            <td className="p-1 sticky left-0 bg-red-100 z-10">💸 DESPESAS</td>
            {Array(15).fill(0).map((_, i) => {
              const valor = calcularDespesaDia(i + 16);
              return (
                <td key={i} className={`p-1 text-right text-red-700 ${i + 17 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                  {i + 17 <= daysInSelectedMonth ? (valor !== 0 ? formatPrice(valor) : '-') : ''}
                </td>
              );
            })}
            <td className="p-1 text-right text-red-700 bg-red-200 font-bold">{formatPrice(totalDespesasMes)}</td>
          </tr>
          
          {/* SALDO */}
          <tr className={`font-bold ${saldoMes >= 0 ? 'bg-blue-200' : 'bg-red-200'}`}>
            <td className={`p-1 sticky left-0 z-10 ${saldoMes >= 0 ? 'bg-blue-200' : 'bg-red-200'}`}>💰 SALDO</td>
            {Array(15).fill(0).map((_, i) => {
              const saldo = calcularSaldoDia(i + 16);
              return (
                <td key={i} className={`p-1 text-right ${saldo >= 0 ? 'text-blue-700' : 'text-red-700'} ${i + 17 > daysInSelectedMonth ? 'bg-gray-800' : ''}`}>
                  {i + 17 <= daysInSelectedMonth ? (saldo !== 0 ? formatPrice(saldo) : '-') : ''}
                </td>
              );
            })}
            <td className={`p-1 text-right font-bold ${saldoMes >= 0 ? 'bg-blue-300 text-blue-800' : 'bg-red-300 text-red-800'}`}>
              {formatPrice(saldoMes)}
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-full mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="hover:text-[#FFD700]">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <DollarSign className="w-6 h-6" />
            <h1 className="text-xl font-bold">Fluxo de Caixa</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => setShowUploadModal(true)} variant="outline" className="text-white border-white hover:bg-white/10">
              <Upload className="w-4 h-4 mr-2" />
              STONE CSV
            </Button>
            
            <Button onClick={() => setShowUploadSumupModal(true)} variant="outline" className="text-white border-pink-300 hover:bg-pink-500/20 text-pink-200">
              <Upload className="w-4 h-4 mr-2" />
              SUMUP Excel
            </Button>
            
            <Button onClick={loadData} variant="outline" size="sm" className="text-white border-white hover:bg-white/10">
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
            
            <Button onClick={exportToCSV} className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-900">
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-full mx-auto px-4 py-6">
        {/* Cards de Resumo */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-700 font-medium">Total Receitas</span>
              </div>
              <p className="text-2xl font-bold text-green-700">{formatPrice(totalReceitasAnual)}</p>
            </CardContent>
          </Card>

          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="w-5 h-5 text-red-600" />
                <span className="text-sm text-red-700 font-medium">Total Despesas</span>
              </div>
              <p className="text-2xl font-bold text-red-700">{formatPrice(totalDespesasAnual)}</p>
            </CardContent>
          </Card>

          <Card className={`${saldoAnual >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-red-100 border-red-300'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className={`w-5 h-5 ${saldoAnual >= 0 ? 'text-blue-600' : 'text-red-600'}`} />
                <span className={`text-sm font-medium ${saldoAnual >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                  Saldo {selectedYear}
                </span>
              </div>
              <p className={`text-2xl font-bold ${saldoAnual >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                {formatPrice(saldoAnual)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <CreditCard className="w-5 h-5 text-purple-600" />
                <span className="text-sm text-purple-700 font-medium">💳 ASAAS</span>
              </div>
              <p className="text-2xl font-bold text-purple-700">{formatPrice(totalAsaasAnual)}</p>
            </CardContent>
          </Card>

          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Building className="w-5 h-5 text-orange-600" />
                <span className="text-sm text-orange-700 font-medium">🏦 INTER</span>
              </div>
              <p className="text-2xl font-bold text-orange-700">{formatPrice(totalInterPagoAnual + totalInterPrevistoAnual)}</p>
            </CardContent>
          </Card>

          <Card className="bg-cyan-50 border-cyan-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <CreditCard className="w-5 h-5 text-cyan-600" />
                <span className="text-sm text-cyan-700 font-medium">💳 STONE</span>
              </div>
              <p className="text-2xl font-bold text-cyan-700">{formatPrice(totalStoneAnual)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs para Anual / Mensal - COM SELETORES DE ANO E MÊS */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <TabsList>
              <TabsTrigger value="anual">📅 Visão Anual</TabsTrigger>
              <TabsTrigger value="mensal">📆 Visão Mensal</TabsTrigger>
            </TabsList>
            
            {/* Seletor de Ano - sempre visível */}
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Seletor de Mês - aparece apenas na visão mensal */}
            {activeTab === 'mensal' && (
              <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS_FULL.map((m, i) => (
                    <SelectItem key={i} value={String(i)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          
          {/* Tabela ANUAL */}
          <TabsContent value="anual">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Fluxo de Caixa Anual - {selectedYear}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-[#003366] text-white">
                        <th className="p-2 text-left font-semibold sticky left-0 bg-[#003366] z-20 min-w-[160px]">Descrição</th>
                        {MONTHS.map((m, idx) => (
                          <th 
                            key={idx} 
                            className={`p-2 text-center font-semibold min-w-[80px] ${
                              isCurrentMonth(idx) ? 'bg-yellow-400 text-gray-900' : ''
                            }`}
                          >
                            {m}
                          </th>
                        ))}
                        <th className="p-2 text-center font-semibold min-w-[100px] bg-[#002244]">TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* RECEITAS */}
                      <tr className="bg-green-100 cursor-pointer hover:bg-green-200 font-bold" onClick={() => setExpandReceitas(!expandReceitas)}>
                        <td className="p-2 sticky left-0 bg-green-100 z-10 flex items-center gap-2">
                          {expandReceitas ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          💰 RECEITAS
                        </td>
                        {Array(12).fill(0).map((_, i) => (
                          <td key={i} className="p-2 text-right text-green-700">{formatPrice(calcularTotalReceitas(i))}</td>
                        ))}
                        <td className="p-2 text-right text-green-700 bg-green-200 font-bold">{formatPrice(totalReceitasAnual)}</td>
                      </tr>
                      
                      {expandReceitas && (
                        <>
                          {/* ASAAS Unificado */}
                          <tr className="bg-purple-50 hover:bg-purple-100">
                            <td className="p-2 pl-8 sticky left-0 bg-purple-50 z-10">💳 ASAAS (PIX + Boleto + Cartão)</td>
                            {Array(12).fill(0).map((_, i) => {
                              const val = calcularTotalReceitasAsaas(i);
                              return <td key={i} className={`p-2 text-right ${val < 0 ? 'text-red-700' : 'text-purple-700'}`}>{val !== 0 ? formatPrice(val) : '-'}</td>;
                            })}
                            <td className="p-2 text-right text-purple-700 bg-purple-100 font-medium">{formatPrice(totalAsaasAnual)}</td>
                          </tr>
                          {/* BOLETOS A VENCER */}
                          <tr className="bg-yellow-50 hover:bg-yellow-100">
                            <td className="p-2 pl-8 sticky left-0 bg-yellow-50 z-10">📄 BOLETOS A VENCER</td>
                            {pendingInstallmentsRevenue.totals.map((val, i) => (
                              <td key={i} className={`p-2 text-right ${val < 0 ? 'text-red-700' : 'text-yellow-700'}`}>{val !== 0 ? formatPrice(val) : '-'}</td>
                            ))}
                            <td className="p-2 text-right text-yellow-700 bg-yellow-100 font-medium">{formatPrice(totalPendingInstallmentsAnual)}</td>
                          </tr>
                          {/* BOLETOS VENCIDOS */}
                          <tr className="bg-red-50 hover:bg-red-100">
                            <td className="p-2 pl-8 sticky left-0 bg-red-50 z-10">⚠️ BOLETOS VENCIDOS</td>
                            {overdueInstallmentsRevenue.totals.map((val, i) => (
                              <td key={i} className={`p-2 text-right ${val > 0 ? 'text-red-600' : 'text-gray-700'}`}>{val !== 0 ? formatPrice(val) : '-'}</td>
                            ))}
                            <td className="p-2 text-right text-red-600 bg-red-100 font-medium">{formatPrice(totalOverdueInstallmentsAnual)}</td>
                          </tr>
                          {/* INTER PAGO */}
                          <tr className="bg-orange-50 hover:bg-orange-100">
                            <td className="p-2 pl-8 sticky left-0 bg-orange-50 z-10">🏦 INTER (Pago)</td>
                            {interRevenues.totals.map((val, i) => (
                              <td key={i} className={`p-2 text-right ${val < 0 ? 'text-red-700' : 'text-orange-700'}`}>{val !== 0 ? formatPrice(val) : '-'}</td>
                            ))}
                            <td className="p-2 text-right text-orange-700 bg-orange-100 font-medium">{formatPrice(totalInterPagoAnual)}</td>
                          </tr>
                          {/* INTER PREVISTO */}
                          <tr className="bg-amber-50 hover:bg-amber-100">
                            <td className="p-2 pl-8 sticky left-0 bg-amber-50 z-10">🏦 INTER (Previsto)</td>
                            {interPrevistoRevenues.totals.map((val, i) => (
                              <td key={i} className={`p-2 text-right ${val < 0 ? 'text-red-700' : 'text-amber-700'}`}>{val !== 0 ? formatPrice(val) : '-'}</td>
                            ))}
                            <td className="p-2 text-right text-amber-700 bg-amber-100 font-medium">{formatPrice(totalInterPrevistoAnual)}</td>
                          </tr>
                          {/* STONE (CSV) */}
                          <tr className="bg-cyan-50 hover:bg-cyan-100">
                            <td className="p-2 pl-8 sticky left-0 bg-cyan-50 z-10">💳 STONE (CSV)</td>
                            {stoneRevenues.totals.map((val, i) => (
                              <td key={i} className={`p-2 text-right ${val < 0 ? 'text-red-700' : 'text-cyan-700'}`}>{val !== 0 ? formatPrice(val) : '-'}</td>
                            ))}
                            <td className="p-2 text-right text-cyan-700 bg-cyan-100 font-medium">{formatPrice(totalStoneAnual)}</td>
                          </tr>
                          {/* SUMUP (Pago) */}
                          <tr className="bg-pink-50 hover:bg-pink-100">
                            <td className="p-2 pl-8 sticky left-0 bg-pink-50 z-10">💳 SUMUP (Pago)</td>
                            {sumupExcelRevenues.pago.totals.map((val, i) => {
                              const total = val + sumupRevenues.pago.totals[i];
                              return <td key={i} className={`p-2 text-right ${total < 0 ? 'text-red-700' : 'text-pink-700'}`}>{total !== 0 ? formatPrice(total) : '-'}</td>;
                            })}
                            <td className="p-2 text-right text-pink-700 bg-pink-100 font-medium">{formatPrice(totalSumupPagoAnual)}</td>
                          </tr>
                          {/* SUMUP (Previsto) */}
                          <tr className="bg-pink-50/50 hover:bg-pink-100">
                            <td className="p-2 pl-8 sticky left-0 bg-pink-50/50 z-10">💳 SUMUP (Previsto)</td>
                            {sumupExcelRevenues.previsto.totals.map((val, i) => (
                              <td key={i} className={`p-2 text-right ${val < 0 ? 'text-red-700' : 'text-pink-600'}`}>{val !== 0 ? formatPrice(val) : '-'}</td>
                            ))}
                            <td className="p-2 text-right text-pink-600 bg-pink-100/50 font-medium">{formatPrice(totalSumupPrevistoAnual)}</td>
                          </tr>
                          {/* ORÇADO (Projeção) */}
                          <tr className="bg-gray-50 hover:bg-gray-100">
                            <td className="p-2 pl-8 sticky left-0 bg-gray-50 z-10">📊 ORÇADO (Projeção)</td>
                            {budgetedRevenues.totals.map((val, i) => (
                              <td key={i} className={`p-2 text-right ${val < 0 ? 'text-red-700' : 'text-gray-600'}`}>{val !== 0 ? formatPrice(val) : '-'}</td>
                            ))}
                            <td className="p-2 text-right text-gray-600 bg-gray-100 font-medium">{formatPrice(totalBudgetedAnual)}</td>
                          </tr>
                        </>
                      )}
                      
                      <tr><td colSpan={14} className="h-2 bg-gray-100"></td></tr>
                      
                      {/* DESPESAS */}
                      <tr className="bg-red-100 cursor-pointer hover:bg-red-200 font-bold" onClick={() => setExpandDespesas(!expandDespesas)}>
                        <td className="p-2 sticky left-0 bg-red-100 z-10 flex items-center gap-2">
                          {expandDespesas ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          💸 DESPESAS
                        </td>
                        {Array(12).fill(0).map((_, i) => (
                          <td key={i} className="p-2 text-right text-red-700">{formatPrice(calcularTotalDespesas(i))}</td>
                        ))}
                        <td className="p-2 text-right text-red-700 bg-red-200 font-bold">{formatPrice(totalDespesasAnual)}</td>
                      </tr>
                      
                      {/* Detalhes das Despesas */}
                      {expandDespesas && (
                        <>
                          <tr className="bg-red-50">
                            <td className="p-1 pl-6 sticky left-0 bg-red-50 z-10">📦 Operacionais</td>
                            {expensesByMonth.totals.map((val, i) => (
                              <td key={i} className="p-1 text-right text-red-600">{val !== 0 ? formatPrice(val) : '-'}</td>
                            ))}
                            <td className="p-1 text-right text-red-600 bg-red-100">{formatPrice(totalDespesasOperacionais)}</td>
                          </tr>
                          <tr className="bg-orange-50">
                            <td className="p-1 pl-6 sticky left-0 bg-orange-50 z-10">🏢 Gerais</td>
                            {generalExpensesByMonth.totals.map((val, i) => (
                              <td key={i} className="p-1 text-right text-orange-600">{val !== 0 ? formatPrice(val) : '-'}</td>
                            ))}
                            <td className="p-1 text-right text-orange-600 bg-orange-100">{formatPrice(totalDespesasGerais)}</td>
                          </tr>
                        </>
                      )}
                      
                      <tr><td colSpan={14} className="h-2 bg-gray-100"></td></tr>
                      
                      {/* SALDO */}
                      <tr className={`font-bold text-lg ${saldoAnual >= 0 ? 'bg-blue-200' : 'bg-red-200'}`}>
                        <td className={`p-3 sticky left-0 z-10 ${saldoAnual >= 0 ? 'bg-blue-200' : 'bg-red-200'}`}>💰 SALDO</td>
                        {Array(12).fill(0).map((_, i) => {
                          const saldo = calcularSaldo(i);
                          return (
                            <td key={i} className={`p-3 text-right ${saldo >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{formatPrice(saldo)}</td>
                          );
                        })}
                        <td className={`p-3 text-right font-bold ${saldoAnual >= 0 ? 'bg-blue-300 text-blue-800' : 'bg-red-300 text-red-800'}`}>{formatPrice(saldoAnual)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Tabela MENSAL */}
          <TabsContent value="mensal">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Fluxo de Caixa Diário - {MONTHS_FULL[selectedMonth]} {selectedYear}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="overflow-x-auto">
                  <MonthlyTablePart1 />
                  <MonthlyTablePart2 />
                </div>
                
                <div className="mt-4 text-xs text-gray-500">
                  <p>• Células pretas = dias que não existem no mês</p>
                  <p>• ASAAS = Receitas de reservas pelo site (PIX, Boleto, Cartão)</p>
                  <p>• INTER = Parcelas de reservas manuais com Banco/Fonte = INTER</p>
                  <p>• STONE = Importado do CSV STONE (não considera parcelas marcadas como STONE)</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal Upload STONE */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>📁 Importar CSV STONE</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-cyan-50 rounded-lg text-sm text-cyan-800">
              <strong>💳 Relatório STONE</strong>
              <p className="mt-1">O sistema vai ler:</p>
              <ul className="list-disc ml-4 mt-1">
                <li><strong>Coluna E</strong> - Data de Vencimento</li>
                <li><strong>Coluna M</strong> - Valor Líquido</li>
              </ul>
            </div>
            
            <div>
              <Label>Arquivo CSV</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="mt-1"
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowUploadModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={handleUploadFile} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
                <Upload className="w-4 h-4 mr-2" />
                Importar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Upload SUMUP Excel */}
      <Dialog open={showUploadSumupModal} onOpenChange={setShowUploadSumupModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-pink-600" />
              Importar SUMUP (Excel)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-pink-50 rounded-lg border border-pink-200 text-sm">
              <p className="font-semibold text-pink-800 mb-2">📊 Formato da Planilha:</p>
              <ul className="text-pink-700 space-y-1">
                <li>• <strong>Aba &quot;previsto&quot;</strong>: Coluna A (Data) + Coluna F (Valor)</li>
                <li>• <strong>Aba &quot;Recebido&quot;</strong>: Coluna I (Data) + Coluna H (Valor)</li>
              </ul>
              <p className="mt-2 text-pink-600">⚠️ O upload substitui todos os dados SUMUP anteriores.</p>
            </div>
            
            <div>
              <Label>Arquivo Excel (.xlsx)</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setUploadSumupFile(e.target.files?.[0] || null)}
                className="mt-1"
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowUploadSumupModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={handleUploadSumupFile} className="flex-1 bg-pink-600 hover:bg-pink-700">
                <Upload className="w-4 h-4 mr-2" />
                Importar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCashFlow;
